// ========================================
// TENANT CONTEXT
// Provides tenant branding and config throughout the app
// Supports: office, warehousing, residential
// frontend/src/contexts/TenantContext.js
// ========================================

import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const TenantContext = createContext();

export const useTenant = () => useContext(TenantContext);

// Default branding (Century - fallback)
const DEFAULT_BRANDING = {
  name: 'Century Real Estate',
  logo_url: '/century-logo.png',
  primary_color: '#111827',
  secondary_color: '#374151',
  tagline: 'Thinking Ahead',
  industry_type: 'office',
  settings: {}
};

// Industry-specific configurations
const INDUSTRY_CONFIG = {
  office: {
    // Labels
    assetLabel: 'Projects',
    assetSingular: 'Project',
    spaceLabel: 'Units',
    spaceSingular: 'Unit',
    buildingLabel: 'Buildings',
    buildingSingular: 'Building',
    amenitiesLabel: 'Facilities',
    amenitiesSingular: 'Facility',
    
    // Metrics displayed
    metrics: ['NLA', 'Floors', 'Floor Plate', 'Parking Ratio'],
    
    // Asset class options
    assetClasses: ['Grade A', 'Grade A+', 'Grade B+', 'SEZ', 'IT Park'],
    
    // Space type options
    spaceTypes: ['Office Unit', 'Retail', 'Cafeteria', 'Common Area', 'Meeting Room'],
    
    // Client industries
    clientIndustries: ['IT/ITES', 'BFSI', 'Consulting', 'Manufacturing', 'Healthcare', 'E-commerce', 'Other'],
    
    // Document categories
    documentCategories: ['Floor Plans', 'Brochure', 'Legal', 'Factsheet', 'ESG', 'Other'],
    
    // Area unit
    areaUnit: 'sq ft',
    areaField: 'total_nla'
  },
  
  warehousing: {
    // Labels
    assetLabel: 'Parks',
    assetSingular: 'Park',
    spaceLabel: 'Units',
    spaceSingular: 'Unit',
    buildingLabel: 'Blocks',
    buildingSingular: 'Block',
    amenitiesLabel: 'Infrastructure',
    amenitiesSingular: 'Infrastructure',
    
    // Metrics displayed
    metrics: ['Covered Area', 'Clear Height', 'Floor Load', 'Dock Doors'],
    
    // Asset class options
    assetClasses: ['Grade A Logistics Park', 'Logistics Park', 'Cold Storage', 'Industrial Park', 'Built-to-Suit', 'Industrial Shed', 'Free Trade Zone'],
    
    // Space type options
    spaceTypes: ['Warehouse Unit', 'Warehouse Bay', 'Cold Room', 'Open Yard', 'Office Area', 'Mezzanine', 'Loading Bay'],
    
    // Client industries
    clientIndustries: ['3PL', 'E-commerce', 'FMCG', 'Manufacturing', 'Retail', 'Automotive', 'Pharma', 'Cold Chain', 'Other'],
    
    // Document categories
    documentCategories: ['Floor Plans', 'Fire NOC', 'Building Permits', 'ISO Certificates', 'Site Plans', 'Legal'],
    
    // Area unit
    areaUnit: 'sq ft',
    areaField: 'covered_area'
  },
  
  residential: {
    // Labels
    assetLabel: 'Projects',
    assetSingular: 'Project',
    spaceLabel: 'Units',
    spaceSingular: 'Unit',
    buildingLabel: 'Towers',
    buildingSingular: 'Tower',
    amenitiesLabel: 'Amenities',
    amenitiesSingular: 'Amenity',
    
    // Metrics displayed
    metrics: ['Total Units', 'Towers', 'Possession Date', 'RERA'],
    
    // Project type options (instead of asset class)
    assetClasses: ['Apartment', 'Villa', 'Plotted Development', 'Township', 'Studio Apartments', 'Penthouse'],
    projectTypes: ['Apartment', 'Villa', 'Plotted Development', 'Township', 'Studio Apartments', 'Penthouse'],
    
    // Unit configurations
    unitConfigurations: ['Studio', '1 BHK', '1.5 BHK', '2 BHK', '2.5 BHK', '3 BHK', '3.5 BHK', '4 BHK', '4.5 BHK', '5 BHK', 'Penthouse', 'Duplex', 'Triplex', 'Villa'],
    
    // Unit facing options
    facingOptions: ['North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West'],
    
    // Unit status options
    unitStatuses: ['Available', 'Sold', 'Blocked', 'Hold'],
    
    // Client industries (buyer types)
    clientIndustries: ['End User', 'Investor', 'NRI', 'Corporate', 'HNI', 'First-time Buyer', 'Other'],
    
    // Document categories
    documentCategories: ['Brochure', 'Floor Plans', 'RERA Certificate', 'Legal Documents', 'Price List', 'Payment Plan', 'Specifications'],
    
    // Area unit
    areaUnit: 'sq ft',
    areaField: 'carpet_area_sqft',
    
    // Pricing fields
    pricingFields: ['base_price_per_sqft', 'floor_rise_per_floor', 'premiums'],
    
    // Amenity categories
    amenityCategories: ['Fitness', 'Recreation', 'Children', 'Convenience', 'Green', 'Security', 'Parking']
  }
};

export const TenantProvider = ({ children }) => {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [industryConfig, setIndustryConfig] = useState(INDUSTRY_CONFIG.office);
  const [planInfo, setPlanInfo] = useState(null);

  // Function to fetch tenant branding
  const fetchTenantBranding = async () => {
    if (!user?.tenantOrgId) {
      // Super admin or no tenant - use defaults
      setTenant(DEFAULT_BRANDING);
      setIndustryConfig(INDUSTRY_CONFIG.office);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/api/tenant/branding');
      const tenantData = response.data;
      
      setTenant({
        ...DEFAULT_BRANDING,
        ...tenantData,
        // Parse settings if it's a string
        settings: typeof tenantData.settings === 'string' 
          ? JSON.parse(tenantData.settings) 
          : tenantData.settings || {}
      });

      // Set industry config based on tenant type
      const industry = tenantData.industry_type || tenantData.settings?.industry || 'office';
      setIndustryConfig(INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.office);

      // Fetch plan info
      try {
        const planResponse = await api.get('/api/plans/current');
        setPlanInfo(planResponse.data);
      } catch (planError) {
        console.warn('Could not fetch plan info:', planError);
      }

    } catch (error) {
      console.error('Failed to fetch tenant branding:', error);
      setTenant(DEFAULT_BRANDING);
      setIndustryConfig(INDUSTRY_CONFIG.office);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tenant branding when user logs in
  useEffect(() => {
    fetchTenantBranding();
  }, [user?.tenantOrgId]);

  // Apply CSS custom properties for theming
  useEffect(() => {
    if (tenant) {
      document.documentElement.style.setProperty('--primary-color', tenant.primary_color || '#111827');
      document.documentElement.style.setProperty('--secondary-color', tenant.secondary_color || tenant.settings?.secondary_color || '#374151');
    }
  }, [tenant]);

  // Get label based on industry (e.g., "Projects" for office, "Parks" for warehousing)
  const getLabel = (key, plural = true) => {
    const labels = {
      asset: plural ? industryConfig.assetLabel : industryConfig.assetSingular,
      space: plural ? industryConfig.spaceLabel : industryConfig.spaceSingular,
      building: plural ? industryConfig.buildingLabel : industryConfig.buildingSingular,
      amenities: plural ? industryConfig.amenitiesLabel : industryConfig.amenitiesSingular
    };
    return labels[key] || key;
  };

  // Get area field name based on industry
  const getAreaField = () => {
    return industryConfig.areaField || 'total_nla';
  };

  // Get area unit
  const getAreaUnit = () => {
    return industryConfig.areaUnit || 'sq ft';
  };

  // Check if feature is available on current plan
  const hasFeature = (featureName) => {
    if (!planInfo?.plan?.features) return true; // Allow if no plan info
    return planInfo.plan.features[featureName] === true;
  };

  // Check if within limit
  const isWithinLimit = (resourceType) => {
    if (!planInfo?.usage?.[resourceType]) return true;
    const usage = planInfo.usage[resourceType];
    return usage.current < usage.max;
  };

  // Get usage percentage
  const getUsagePercentage = (resourceType) => {
    if (!planInfo?.usage?.[resourceType]) return 0;
    return planInfo.usage[resourceType].percentage || 0;
  };

  return (
    <TenantContext.Provider value={{
      // Tenant info
      tenant,
      loading,
      refetchTenant: fetchTenantBranding,

      // Brand fonts — custom webfonts this tenant brings (e.g. HoH Zona Pro).
      // Each: { slug, label }. Empty for tenants without any. Surfaced into
      // the typography font pickers as "<label> (Brand Font)" options.
      brandFonts: Array.isArray(tenant?.brand_fonts) ? tenant.brand_fonts : [],
      
      // Industry config
      industryConfig,
      getLabel,
      getAreaField,
      getAreaUnit,
      
      // Industry type flags
      isOffice: tenant?.industry_type === 'office' || !tenant?.industry_type,
      isWarehousing: tenant?.industry_type === 'warehousing',
      isResidential: tenant?.industry_type === 'residential',
      
      // Plan info
      planInfo,
      hasFeature,
      isWithinLimit,
      getUsagePercentage,
      
      // Refresh function
      refreshPlanInfo: async () => {
        try {
          const response = await api.get('/api/plans/current');
          setPlanInfo(response.data);
        } catch (error) {
          console.error('Failed to refresh plan info:', error);
        }
      }
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export { INDUSTRY_CONFIG };